/** Sanitized, bounded operational observations. No response/request bodies or URLs are sent. */
export type Observation = { service: string; operation: string; status: number };
export function classifyServiceRequest(value: string, base: string, ownOrigin: string): Omit<Observation, 'status'> | null {
  let url: URL;
  try { url = new URL(value, ownOrigin); } catch { return null; }
  if (!/^https?:$/.test(url.protocol)) return null;
  if (url.origin === new URL(base).origin) {
    if (url.pathname.includes('/rpc/report_service_error') || url.pathname.includes('/rpc/report_audio_health_event') || url.pathname === '/functions/v1/admin-error-logs'
      || url.pathname === '/functions/v1/tts-proxy/health') return null; // Dedicated health diagnostics include timed-out probes and their later recovery.
    if (url.pathname.startsWith('/auth/')) return { service: 'Supabase Auth', operation: 'authentication' };
    if (url.pathname.startsWith('/storage/')) return { service: 'Supabase Storage', operation: 'storage' };
    const fn = url.pathname.match(/^\/functions\/v1\/([a-z][a-z0-9-]{0,63})(?:\/|$)/)?.[1];
    return { service: 'Supabase', operation: fn ?? 'database' };
  }
  if (url.origin === ownOrigin) return { service: 'Cloudflare', operation: 'site-request' };
  if (url.hostname.endsWith('.runpod.net') || url.hostname === 'api.runpod.io') return { service: 'RunPod', operation: 'worker-request' };
  return null;
}
export function createObservedFetch(original: typeof fetch, classify: (url: string) => Omit<Observation, 'status'> | null,
  report: (event: Observation) => Promise<void>, now = Date.now): typeof fetch {
  const seen = new Map<string, { at: number; failed: boolean }>();
  const emit = (event: Observation) => {
    const key = `${event.service}:${event.operation}`, previous = seen.get(key), at = now();
    if (event.status === 200 && !previous?.failed) return;
    if (event.status !== 200 && previous?.failed && at - previous.at < 60_000) return;
    if (seen.size >= 200 && !seen.has(key)) return;
    seen.set(key, { at, failed: event.status !== 200 });
    void report(event).catch(() => {}); // Observability must never break or retry product requests.
  };
  return async (input, init) => {
    const route = classify(typeof input === 'string' ? input : input instanceof URL ? input.href : input.url);
    try {
      const result = await original(input, init);
      if (route) emit({ ...route, status: result.status >= 400 ? result.status : 200 });
      return result;
    } catch (error) {
      // User Stop/navigation/timeout AbortSignals are not necessarily service outages.
      const signal = init?.signal ?? (input instanceof Request ? input.signal : undefined);
      if (route && !signal?.aborted) emit({ ...route, status: 0 });
      throw error;
    }
  };
}
export function installServiceErrorCapture(base: string, publishableKey: string, getToken: () => Promise<string | null>): void {
  const original = window.fetch.bind(window);
  let reporting = false;
  const reported = new Map<string, number>();
  let windowStart = Date.now(), reports = 0;
  const report = async (event: Observation) => {
    if (reporting) return;
    const at = Date.now(), key = `${event.service}:${event.operation}:${event.status}`;
    if (at-windowStart>=3600000) { windowStart=at; reports=0; reported.clear(); }
    if (reports>=120 || (reported.has(key) && at-reported.get(key)!<60000)) return;
    reported.set(key,at); reports++;
    reporting = true;
    try {
      const token = await getToken();
      if (!token) return; // Do not create an unauthenticated public log-ingestion endpoint.
      await original(`${base}/rest/v1/rpc/report_service_error`, {
        method: 'POST', headers: { apikey: publishableKey, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ p_service: event.service, p_operation: event.operation, p_status: event.status }),
        signal: AbortSignal.timeout(3000),
      });
    } catch { /* No retries, credential storage, or recursive reporting. */ }
    finally { reporting = false; }
  };
  window.fetch = createObservedFetch(original, value => classifyServiceRequest(value, base, window.location.origin), report);
  window.addEventListener('error', event => { if (event instanceof ErrorEvent) void report({ service: 'Browser', operation: 'runtime-error', status: 0 }); });
  window.addEventListener('unhandledrejection', () => { void report({ service: 'Browser', operation: 'unhandled-rejection', status: 0 }); });
}
